import type { MeshData, ParsedStl } from './types';

function startsWithSolid(bytes: Uint8Array): boolean {
  if (bytes.length < 5) return false;
  return (
    bytes[0] === 0x73 && // s
    bytes[1] === 0x6f && // o
    bytes[2] === 0x6c && // l
    bytes[3] === 0x69 && // i
    bytes[4] === 0x64 // d
  );
}

function parseBinary(buffer: ArrayBuffer, triangleCount: number): MeshData {
  const view = new DataView(buffer);
  const positions: number[] = [];
  const indices: number[] = [];
  let offset = 84;
  let kept = 0;

  for (let t = 0; t < triangleCount; t++) {
    if (offset + 50 > buffer.byteLength) break;
    offset += 12; // normalna — liczymy ją ponownie przy eksporcie

    const values = new Array<number>(9);
    let finite = true;
    for (let k = 0; k < 9; k++) {
      const value = view.getFloat32(offset + k * 4, true);
      values[k] = value;
      if (!Number.isFinite(value)) finite = false;
    }
    offset += 36 + 2; // wierzchołki + atrybuty

    if (!finite) continue;
    const base = kept * 3;
    for (let k = 0; k < 9; k++) positions.push(values[k]);
    indices.push(base, base + 1, base + 2);
    kept++;
  }

  if (kept === 0) throw new Error('Plik STL nie zawiera poprawnych trójkątów.');
  return { positions: new Float32Array(positions), indices: new Uint32Array(indices) };
}

function parseAscii(bytes: Uint8Array): MeshData {
  const text = new TextDecoder().decode(bytes);
  const re = /vertex\s+([^\s]+)\s+([^\s]+)\s+([^\s]+)/gi;
  const positions: number[] = [];
  const indices: number[] = [];
  let kept = 0;

  let match: RegExpExecArray | null;
  while ((match = re.exec(text))) {
    const x = Number(match[1]);
    const y = Number(match[2]);
    const z = Number(match[3]);
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) continue;
    positions.push(x, y, z);
    if (kept % 3 === 2) indices.push(kept - 2, kept - 1, kept);
    kept++;
  }

  if (kept < 3 || indices.length === 0) {
    throw new Error('Plik ASCII STL nie zawiera poprawnych trójkątów.');
  }

  // Odrzuć niedokończony trójkąt (uszkodzony plik).
  if (kept % 3 !== 0) {
    const cut = Math.floor(kept / 3) * 3;
    return {
      positions: new Float32Array(positions.slice(0, cut * 3)),
      indices: new Uint32Array(indices),
    };
  }

  return { positions: new Float32Array(positions), indices: new Uint32Array(indices) };
}

/** Wczytuje STL binarny lub ASCII. */
export function parseStl(buffer: ArrayBuffer): ParsedStl {
  if (buffer.byteLength < 15) throw new Error('Plik jest za mały, żeby być plikiem STL.');

  const bytes = new Uint8Array(buffer);
  let binary = false;
  let binaryCount = 0;

  if (buffer.byteLength >= 84) {
    const view = new DataView(buffer);
    binaryCount = view.getUint32(80, true);
    const expected = 84 + binaryCount * 50;
    if (binaryCount > 0 && expected === buffer.byteLength) binary = true;
    else if (binaryCount > 0 && expected <= buffer.byteLength && !startsWithSolid(bytes)) binary = true;
  }

  if (binary) {
    return { mesh: parseBinary(buffer, binaryCount), format: 'binary' };
  }

  const text = new TextDecoder().decode(bytes);
  if (!/vertex/i.test(text)) {
    // Ostatnia szansa: plik binarny z błędnym licznikiem / dodatkowymi bajtami.
    if (buffer.byteLength >= 84) {
      const view = new DataView(buffer);
      const count = view.getUint32(80, true);
      const expected = 84 + count * 50;
      if (count > 0 && expected <= buffer.byteLength) {
        return { mesh: parseBinary(buffer, count), format: 'binary' };
      }
    }
    throw new Error('Nie rozpoznano formatu pliku (STL binarny/ASCII).');
  }

  return { mesh: parseAscii(bytes), format: 'ascii' };
}

/** Zapisuje siatkę jako binarny STL z przeliczonymi normalnymi. */
export function writeBinaryStl(mesh: MeshData, header = 'mesh-repair binary STL'): ArrayBuffer {
  const triangleCount = Math.floor(mesh.indices.length / 3);
  const buffer = new ArrayBuffer(84 + triangleCount * 50);
  const view = new DataView(buffer);
  const bytes = new Uint8Array(buffer);
  const encoded = new TextEncoder().encode(header);
  bytes.set(encoded.subarray(0, 80), 0);
  view.setUint32(80, triangleCount, true);

  let offset = 84;
  const p = mesh.positions;
  for (let t = 0; t < triangleCount; t++) {
    const ia = mesh.indices[t * 3] * 3;
    const ib = mesh.indices[t * 3 + 1] * 3;
    const ic = mesh.indices[t * 3 + 2] * 3;

    const ax = p[ia];
    const ay = p[ia + 1];
    const az = p[ia + 2];
    const bx = p[ib];
    const by = p[ib + 1];
    const bz = p[ib + 2];
    const cx = p[ic];
    const cy = p[ic + 1];
    const cz = p[ic + 2];

    const ux = bx - ax;
    const uy = by - ay;
    const uz = bz - az;
    const vx = cx - ax;
    const vy = cy - ay;
    const vz = cz - az;

    let nx = uy * vz - uz * vy;
    let ny = uz * vx - ux * vz;
    let nz = ux * vy - uy * vx;
    const len = Math.hypot(nx, ny, nz);
    if (len > 0) {
      nx /= len;
      ny /= len;
      nz /= len;
    } else {
      nx = 0;
      ny = 0;
      nz = 0;
    }

    view.setFloat32(offset, nx, true);
    view.setFloat32(offset + 4, ny, true);
    view.setFloat32(offset + 8, nz, true);
    view.setFloat32(offset + 12, ax, true);
    view.setFloat32(offset + 16, ay, true);
    view.setFloat32(offset + 20, az, true);
    view.setFloat32(offset + 24, bx, true);
    view.setFloat32(offset + 28, by, true);
    view.setFloat32(offset + 32, bz, true);
    view.setFloat32(offset + 36, cx, true);
    view.setFloat32(offset + 40, cy, true);
    view.setFloat32(offset + 44, cz, true);
    view.setUint16(offset + 48, 0, true);

    offset += 50;
  }

  return buffer;
}
